import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicAuthLayout, { PublicAuthAlert, PublicAuthSpinner } from '../components/PublicAuthLayout';
import { getCurrentUser, login, resendVerification, selectTenant, verify2FaLogin } from '../services/api';
import { isTenantSelectionRequired, isTwoFactorRequired, TenantMembership, User } from '../types';

interface LoginProps {
  onLogin: (user: User) => void;
}

type LoginBlock = 'suspended' | 'unverified' | null;

export default function Login({ onLogin }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [blockedState, setBlockedState] = useState<LoginBlock>(null);
  const [verificationUrl, setVerificationUrl] = useState('');
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [pendingTenants, setPendingTenants] = useState<TenantMembership[]>([]);
  const [twoFaToken, setTwoFaToken] = useState<string | null>(null);
  const [twoFaCode, setTwoFaCode] = useState('');

  async function finishLogin(fallbackUser: User) {
    try {
      const currentUser = await getCurrentUser();
      onLogin(currentUser);
    } catch {
      onLogin(fallbackUser);
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setBlockedState(null);
    setVerificationUrl('');
    setLoading(true);

    try {
      const data = await login(email, password);
      if (isTwoFactorRequired(data)) {
        setTwoFaToken(data.token);
        setTwoFaCode('');
        setPassword('');
        setNotice('Two-factor verification is required for this account.');
      } else if (isTenantSelectionRequired(data)) {
        setPendingToken(data.pending_token);
        setPendingTenants(data.tenants);
      } else {
        await finishLogin(data.user);
      }
    } catch (err: any) {
      const res = err.response?.data;
      if (res?.code === 'email_verification_required') {
        setBlockedState('unverified');
        setError('Please verify your email before signing in. You can resend the verification message below.');
      } else if (res?.code === 'account_suspended') {
        setBlockedState('suspended');
        setError('This account has been suspended. Please contact Seedronics support to restore access.');
      } else {
        setError(res?.error || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTwoFactor = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!twoFaToken) return;
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const data = await verify2FaLogin(twoFaToken, twoFaCode.trim());
      if (isTenantSelectionRequired(data)) {
        setTwoFaToken(null);
        setTwoFaCode('');
        setPendingToken(data.pending_token);
        setPendingTenants(data.tenants);
      } else if (data.user) {
        await finishLogin(data.user);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to verify this code. Check your authenticator app or backup code.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTenant = async (tenant: TenantMembership) => {
    if (!pendingToken || !tenant.is_active) return;
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const data = await selectTenant(pendingToken, tenant.tenant_id);
      await finishLogin(data.user);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const targetEmail = email.trim();
    if (!targetEmail) {
      setError('Enter your email address first so we know where to send the verification link.');
      return;
    }

    setResending(true);
    setNotice('');
    setVerificationUrl('');
    try {
      const data = await resendVerification(targetEmail);
      setNotice(data.message || 'If this account is unverified, a new verification email is on its way.');
      if (data.verification_url) setVerificationUrl(data.verification_url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to resend verification email right now.');
    } finally {
      setResending(false);
    }
  };

  const handleBackToSignIn = () => {
    setPendingToken(null);
    setPendingTenants([]);
    setTwoFaToken(null);
    setTwoFaCode('');
    setError('');
    setNotice('');
    setBlockedState(null);
    setVerificationUrl('');
  };

  return (
    <PublicAuthLayout
      asideBody="Sign in to continue monitoring your sites, alerts, reports, and maintenance priorities in one secure command center."
      asideTitle={twoFaToken ? 'Confirm it is you.' : pendingToken ? 'Choose your workspace.' : 'Welcome back.'}
      eyebrow="Seedronics Cloud access"
      footer={
        <>
          New to Seedronics? <Link to="/signup">Get started</Link>
        </>
      }
      title={
        twoFaToken
          ? 'Enter your two-factor code.'
          : pendingToken
            ? 'Select the workspace you want to open.'
            : 'Sign in to your operations workspace.'
      }
      subtitle={
        twoFaToken
          ? 'Use your authenticator app code or one of your saved backup codes.'
          : 'Use the work email connected to your Seedronics workspace.'
      }
    >
      {error && (
        <PublicAuthAlert tone={blockedState === 'suspended' ? 'warning' : 'error'}>
          <span>{error}</span>
          {blockedState === 'suspended' && (
            <small>
              Contact <a href="mailto:admin@seedronics.com">admin@seedronics.com</a> or call{' '}
              <a href="tel:+2348079744515">+234 807 974 4515</a>.
            </small>
          )}
          {blockedState === 'unverified' && (
            <button disabled={resending} onClick={handleResendVerification} type="button">
              {resending ? 'Sending...' : 'Resend verification email'}
            </button>
          )}
        </PublicAuthAlert>
      )}

      {notice && (
        <PublicAuthAlert tone="success">
          <span>{notice}</span>
          {verificationUrl && <a href={verificationUrl}>Open verification link</a>}
        </PublicAuthAlert>
      )}

      {twoFaToken ? (
        <form className="sd-public-auth-form" onSubmit={handleVerifyTwoFactor}>
          <label>
            Verification code
            <input
              autoComplete="one-time-code"
              autoFocus
              onChange={(event) => setTwoFaCode(event.target.value)}
              placeholder="123456 or backup code"
              required
              type="text"
              value={twoFaCode}
            />
          </label>
          <div className="sd-public-auth-row">
            <button className="sd-public-auth-link-button" onClick={handleBackToSignIn} type="button">
              Back to sign in
            </button>
          </div>
          <button className="sd-public-auth-submit" disabled={loading || twoFaCode.trim().length < 6} type="submit">
            {loading ? <PublicAuthSpinner /> : 'Verify and sign in'}
          </button>
        </form>
      ) : pendingToken ? (
        <div className="sd-public-workspace-list">
          {pendingTenants.map((tenant) => (
            <button
              disabled={!tenant.is_active || loading}
              key={tenant.tenant_id}
              onClick={() => handleSelectTenant(tenant)}
              type="button"
            >
              <span>
                <strong>{tenant.tenant_name}</strong>
                <small>{tenant.tenant_id}</small>
              </span>
              <em className={tenant.is_active ? 'is-active' : 'is-suspended'}>
                {tenant.is_active ? tenant.role : 'Suspended'}
              </em>
            </button>
          ))}
          <button className="sd-public-auth-link-button" onClick={handleBackToSignIn} type="button">
            Back to sign in
          </button>
        </div>
      ) : (
        <form className="sd-public-auth-form" onSubmit={handleSubmit}>
          <label>
            Email
            <input
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              required
              type="email"
              value={email}
            />
          </label>
          <label>
            Password
            <input
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter your password"
              required
              type="password"
              value={password}
            />
          </label>
          <div className="sd-public-auth-row">
            <Link to="/forgot-password">Forgot password?</Link>
            <button className="sd-public-auth-link-button" onClick={handleResendVerification} type="button">
              Resend verification
            </button>
          </div>
          <button className="sd-public-auth-submit" disabled={loading} type="submit">
            {loading ? <PublicAuthSpinner /> : 'Sign in'}
          </button>
        </form>
      )}
    </PublicAuthLayout>
  );
}
